import {
  Box,
  Grid,
  HStack,
  Icon,
  Image,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { FiChevronRight } from "react-icons/fi";
import type { AlgoliaAdCampaignObject } from "common/interfaces/Algolia";
import { PERFORMANCE_TIER_COLOR_SCHEME } from "../utils";

const GRID_GAP = 4;

function StatusIcon({ status }: { status: string }) {
  if (status === "SCHEDULED") {
    return (
      <Box as="svg" width="10px" height="10px" aria-hidden>
        <path
          d="M5 0C4.01109 0 3.0444 0.293245 2.22215 0.842652C1.3999 1.39206 0.759043 2.17295 0.380605 3.08658C0.00216642 4.00021 -0.0968502 5.00555 0.0960758 5.97545C0.289002 6.94536 0.765206 7.83627 1.46447 8.53553C2.16373 9.23479 3.05465 9.711 4.02455 9.90392C4.99445 10.0969 5.99979 9.99783 6.91342 9.6194C7.82705 9.24096 8.60794 8.60009 9.15735 7.77785C9.70676 6.9556 10 5.98891 10 5C9.9986 3.67435 9.47137 2.40339 8.53399 1.46601C7.59661 0.528633 6.32565 0.00139991 5 0ZM7.69231 5.38461H5C4.89799 5.38461 4.80017 5.34409 4.72804 5.27196C4.65591 5.19983 4.61539 5.10201 4.61539 5V2.30769C4.61539 2.20569 4.65591 2.10786 4.72804 2.03573C4.80017 1.9636 4.89799 1.92308 5 1.92308C5.10201 1.92308 5.19984 1.9636 5.27197 2.03573C5.34409 2.10786 5.38462 2.20569 5.38462 2.30769V4.61538H7.69231C7.79431 4.61538 7.89214 4.65591 7.96427 4.72803C8.0364 4.80016 8.07692 4.89799 8.07692 5C8.07692 5.10201 8.0364 5.19983 7.96427 5.27196C7.89214 5.34409 7.79431 5.38461 7.69231 5.38461Z"
          fill="#4A5568"
        />
      </Box>
    );
  }

  return (
    <Box as="svg" width="10px" height="10px" aria-hidden>
      <path
        d="M5 0C4.01109 0 3.0444 0.293245 2.22215 0.842652C1.3999 1.39206 0.759043 2.17295 0.380605 3.08658C0.00216642 4.00021 -0.0968502 5.00555 0.0960758 5.97545C0.289002 6.94536 0.765206 7.83627 1.46447 8.53553C2.16373 9.23479 3.05465 9.711 4.02455 9.90392C4.99445 10.0969 5.99979 9.99783 6.91342 9.6194C7.82705 9.24096 8.60794 8.60009 9.15735 7.77785C9.70676 6.9556 10 5.98891 10 5C9.99835 3.67442 9.47103 2.40362 8.53371 1.46629C7.59638 0.528969 6.32558 0.00165387 5 0ZM4.23077 6.53846C4.23077 6.64047 4.19025 6.7383 4.11812 6.81042C4.04599 6.88255 3.94816 6.92308 3.84616 6.92308C3.74415 6.92308 3.64632 6.88255 3.57419 6.81042C3.50206 6.7383 3.46154 6.64047 3.46154 6.53846V3.46154C3.46154 3.35953 3.50206 3.2617 3.57419 3.18957C3.64632 3.11744 3.74415 3.07692 3.84616 3.07692C3.94816 3.07692 4.04599 3.11744 4.11812 3.18957C4.19025 3.2617 4.23077 3.35953 4.23077 3.46154V6.53846ZM6.53846 6.53846C6.53846 6.64047 6.49794 6.7383 6.42581 6.81042C6.35368 6.88255 6.25585 6.92308 6.15385 6.92308C6.05184 6.92308 5.95401 6.88255 5.88188 6.81042C5.80975 6.7383 5.76923 6.64047 5.76923 6.53846V3.46154C5.76923 3.35953 5.80975 3.2617 5.88188 3.18957C5.95401 3.11744 6.05184 3.07692 6.15385 3.07692C6.25585 3.07692 6.35368 3.11744 6.42581 3.18957C6.49794 3.2617 6.53846 3.35953 6.53846 3.46154V6.53846Z"
        fill="#4A5568"
      />
    </Box>
  );
}

function MetricCell({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <VStack align="center" spacing={0} minW={0}>
      <Text
        fontSize="xs"
        color="gray.500"
        textTransform="uppercase"
        fontWeight="600"
      >
        {label}
      </Text>
      <Text
        noOfLines={1}
        color="#1A202C"
        fontSize="1rem"
        fontStyle="normal"
        fontWeight={400}
        lineHeight="100%"
      >
        {children}
      </Text>
    </VStack>
  );
}

function InsightsCellContent({ row }: { row: AlgoliaAdCampaignObject }) {
  const summaries = row.intent_summaries ?? [];
  const primary = row.primary_intent_summary;
  const hasSummaries = summaries.length > 0;
  const fallbackPrimary = primary != null && primary !== "" ? primary : null;

  if (hasSummaries) {
    if (summaries.length === 1) {
      return (
        <Text
          noOfLines={1}
          overflow="hidden"
          textOverflow="ellipsis"
          minW={0}
          color="gray.600"
          fontSize="0.875rem"
          fontStyle="normal"
          fontWeight={600}
          lineHeight="130%"
        >
          {summaries[0]}
        </Text>
      );
    }
    const label = `${summaries.length} Optimizations`;
    return (
      <Tooltip
        label={
          <VStack align="stretch" spacing={1} py={1}>
            {summaries.map((s, i) => (
              <Text key={i} fontSize="sm" whiteSpace="normal">
                {s}
              </Text>
            ))}
          </VStack>
        }
        hasArrow
        placement="top"
        maxW="320px"
      >
        <Text
          noOfLines={1}
          overflow="hidden"
          textOverflow="ellipsis"
          minW={0}
          cursor="default"
          color="gray.600"
          fontSize="0.875rem"
          fontStyle="normal"
          fontWeight={600}
          lineHeight="130%"
        >
          {label}
        </Text>
      </Tooltip>
    );
  }

  return (
    <Text
      noOfLines={1}
      overflow="hidden"
      textOverflow="ellipsis"
      minW={0}
      color="gray.600"
      fontSize="0.875rem"
      fontStyle="normal"
      fontWeight={600}
      lineHeight="130%"
    >
      {fallbackPrimary ?? "—"}
    </Text>
  );
}

export default function CampaignActiveRow({
  row,
  subtitle,
  eventDateLabel,
  status = "ACTIVE",
  showPerformanceIndicator = true,
  tableGridTemplate,
}: {
  row: AlgoliaAdCampaignObject;
  subtitle: string;
  eventDateLabel?: string;
  status?: string;
  showPerformanceIndicator?: boolean;
  tableGridTemplate?: string;
}) {
  // Dot color from performance_tier; same tokens as legend (CampaignsTableHeader LEGEND_ITEMS).
  const dotColor =
    PERFORMANCE_TIER_COLOR_SCHEME[row.performance_tier ?? ""] ?? "gray.400";

  return (
    <Link to={`/campaigns/ads/${row.objectID}`}>
      <Grid
        px={4}
        py={3}
        alignItems="center"
        gap={GRID_GAP}
        templateColumns={tableGridTemplate}
        _hover={{ bg: "gray.50" }}
        minW={0}
      >
        <Box width="64px" height="64px" position="relative">
          <Image
            src={row.image_url}
            width="64px"
            height="64px"
            alt={row.name}
            borderRadius="md"
            objectFit="cover"
          />
          <Box
            position="absolute"
            inset={0}
            borderRadius="md"
            border="1px solid #DDDDDD"
            pointerEvents="none"
          />
          {status !== "ACTIVE" ? (
            <>
              <Box
                position="absolute"
                inset={0}
                borderRadius="md"
                bg="rgba(43, 53, 72, 0.45)"
                pointerEvents="none"
              />
              <Box
                position="absolute"
                inset={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                pointerEvents="none"
              >
                <Box as="svg" viewBox="0 0 56 56" boxSize={8} aria-hidden>
                  <path
                    fill="white"
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M28 56C43.464 56 56 43.464 56 28C56 12.536 43.464 0 28 0C12.536 0 0 12.536 0 28C0 43.464 12.536 56 28 56ZM22.4 19.6C21.0745 19.6 20 20.6745 20 22V34C20 35.3255 21.0745 36.4 22.4 36.4C23.7255 36.4 24.8 35.3255 24.8 34V22C24.8 20.6745 23.7255 19.6 22.4 19.6ZM33.6 19.6C32.2745 19.6 31.2 20.6745 31.2 22V34C31.2 35.3255 32.2745 36.4 33.6 36.4C34.9255 36.4 36 35.3255 36 34V22C36 20.6745 34.9255 19.6 33.6 19.6Z"
                  />
                </Box>
              </Box>
            </>
          ) : null}
        </Box>
        <VStack align="stretch" spacing={0} overflow="hidden">
          <HStack align="center" gap={2}>
            {status !== "ACTIVE" ? (
              <StatusIcon status={status} />
            ) : (
              showPerformanceIndicator && (
                <Box
                  w={2}
                  h={2}
                  borderRadius="full"
                  bg={dotColor}
                  flexShrink={0}
                />
              )
            )}
            <Text
              fontWeight="semibold"
              fontSize="sm"
              noOfLines={1}
              flex={1}
              overflow="hidden"
              textOverflow="ellipsis"
              wordBreak="break-all"
            >
              {row.name}
            </Text>
          </HStack>
          {eventDateLabel ? (
            <Text fontSize="xs" color="gray.600" noOfLines={1}>
              {eventDateLabel}
            </Text>
          ) : null}
          <Text
            fontSize="xs"
            color="gray.600"
            fontWeight={status === "SCHEDULED" ? "600" : "400"}
            noOfLines={1}
          >
            {subtitle}
          </Text>
        </VStack>
        <VStack align="flex-start" spacing={0} minW={0}>
          <Text
            fontSize="xs"
            color="gray.500"
            textTransform="uppercase"
            fontWeight="600"
          >
            Budget spent
          </Text>
          <Text
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            minW={0}
            title={`${row.display_spend ?? "—"}${
              row.display_budget != null && row.display_budget !== ""
                ? ` / ${row.display_budget}`
                : ""
            }`}
            color="#1A202C"
            fontSize="1rem"
            fontStyle="normal"
            fontWeight={400}
            lineHeight="100%"
          >
            <Text as="span" color="#1A202C">
              {row.display_spend ?? "—"}
            </Text>
            {row.display_budget != null && row.display_budget !== "" && (
              <Text as="span" color="#7B8B9E">
                {" "}
                / {row.display_budget}
              </Text>
            )}
          </Text>
        </VStack>
        <MetricCell label="ROAS">{row.display_roas ?? "—"}</MetricCell>
        <MetricCell label="Purchases">{row.display_tickets ?? "—"}</MetricCell>
        <VStack align="flex-end" spacing={0} minW={0} overflow="hidden">
          <Text
            fontSize="xs"
            color="gray.500"
            textTransform="uppercase"
            fontWeight="600"
          >
            Insights
          </Text>
          <HStack
            minW={0}
            overflow="hidden"
            justify="flex-end"
            align="center"
            gap={2}
          >
            <InsightsCellContent row={row} />
            <Icon
              as={FiChevronRight}
              color="gray.400"
              boxSize={4}
              flexShrink={0}
            />
          </HStack>
        </VStack>
      </Grid>
    </Link>
  );
}
